import { useState, useId, useEffect, type ReactNode } from 'react'
import {
  BlockStack,
  Card,
  Text,
  InlineStack,
  Button,
  Box,
  Collapsible,
  Spinner,
  Icon,
  Popover,
  ActionList,
  Tooltip,
} from '@shopify/polaris'
import {
  MenuHorizontalIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XSmallIcon,
} from '@shopify/polaris-icons'

export type ChecklistProps = {
  title?: string
  description?: string
  items: ChecklistItemProps[]
  onDismiss?: () => void
}

export const Checklist = ({
  title,
  description,
  items,
  onDismiss,
}: ChecklistProps) => {
  const [isOpen, setIsOpen] = useState(true)
  const [popoverActive, setPopoverActive] = useState(false)

  const accessId = useId()

  const firstIncompleteItem = items.find((item) => item.status !== 'COMPLETE')

  return (
    <div className="checklistWrapper">
      <Card padding="0">
        <Box padding="400">
          <BlockStack gap="100">
            <InlineStack align="space-between" blockAlign="center">
              {title && (
                <InlineStack>
                  <Text as="h3" variant="headingMd">
                    <span>{title}</span>
                  </Text>
                </InlineStack>
              )}
              <InlineStack gap="200">
                {onDismiss && (
                  <Popover
                    active={popoverActive}
                    onClose={() => setPopoverActive((prev) => !prev)}
                    activator={
                      <InlineStack>
                        <Button
                          onClick={() => setPopoverActive((prev) => !prev)}
                          variant="tertiary"
                          size="slim"
                          icon={MenuHorizontalIcon}
                        />
                      </InlineStack>
                    }
                  >
                    <ActionList
                      actionRole="menuitem"
                      items={[
                        {
                          content: 'Dismiss',
                          onAction: onDismiss,
                          prefix: <Icon tone="subdued" source={XSmallIcon} />,
                        },
                      ]}
                    />
                  </Popover>
                )}

                <Button
                  variant="tertiary"
                  size="slim"
                  icon={isOpen ? ChevronUpIcon : ChevronDownIcon}
                  onClick={() => {
                    setIsOpen((prev) => {
                      return !prev
                    })
                  }}
                  ariaControls={accessId}
                />
              </InlineStack>
            </InlineStack>
            {description && (
              <Text as="p" variant="bodyMd">
                {description}
              </Text>
            )}
          </BlockStack>
        </Box>
        <Collapsible open={isOpen} id={accessId}>
          <Box padding="200" paddingBlockStart="0">
            <BlockStack gap="100">
              {items.map((item) => {
                return (
                  <ChecklistItem
                    key={`checklist-item-${item.id.toString()}`}
                    {...item}
                    isDefaultOpen={item.id === firstIncompleteItem?.id}
                  />
                )
              })}
            </BlockStack>
          </Box>
        </Collapsible>
      </Card>
    </div>
  )
}

export type ChecklistItemProps = {
  id: string
  title: string
  description?: string
  status: 'COMPLETE' | 'INCOMPLETE' | 'IN_PROGRESS'
  primaryButton?: {
    content?: string
    visible?: boolean
    props: {
      url?: string
      external?: boolean
      onClick?: () => void
      disabled?: boolean
      loading?: boolean
    }
  }
  secondaryButton?: {
    content?: string
    visible?: boolean
    props: {
      url?: string
      external?: boolean
      onClick?: () => void
      disabled?: boolean
    }
  }
  illustration?: ReactNode
}

export type ChecklistItemPropsWithOpen = ChecklistItemProps & {
  isDefaultOpen: boolean
  onOpen?: (id: string) => void
  iconAction?: () => void
}

export const ChecklistItem = ({
  id,
  status,
  title,
  description,
  primaryButton,
  secondaryButton,
  illustration,
  isDefaultOpen,
  onOpen,
  iconAction,
}: ChecklistItemPropsWithOpen) => {
  const [isOpen, setIsOpen] = useState(isDefaultOpen)

  const toggleOpen = () => {
    setIsOpen((prev) => !prev)
    if (onOpen) {
      onOpen(id)
    }
  }

  const Icon = () => {
    if (status === 'IN_PROGRESS') {
      return <Spinner size="small" />
    }

    if (status === 'COMPLETE') {
      return filledSvg
    }

    return outlineSvg
  }

  const hasCollapsible =
    description || primaryButton || secondaryButton || illustration

  useEffect(() => {
    setIsOpen(isDefaultOpen)
  }, [isDefaultOpen])

  return (
    <div
      className={`checklistItem illustrationCardWrapper ${
        isOpen && 'is--open'
      }`}
      aria-label={title}
    >
      <div className="checklistItemIcon">
        {iconAction ? (
          <Tooltip
            content={
              status === 'COMPLETE' ? 'Mark as not done' : 'Mark as done'
            }
          >
            <button
              type="button"
              className="checklistItemIconButton"
              onClick={() => iconAction()}
            >
              <Icon />
            </button>
          </Tooltip>
        ) : (
          <span className="checklistItemIconStatic">
            <Icon />
          </span>
        )}
      </div>
      <button
        type="button"
        className="checklistItemButton"
        disabled={!hasCollapsible || isOpen}
        onClick={toggleOpen}
        aria-controls={id}
        aria-expanded={isOpen}
        aria-disabled={!hasCollapsible || isOpen}
      >
        <InlineStack gap="200" blockAlign="center">
          <Text
            as="span"
            variant="bodyMd"
            fontWeight={isOpen ? 'semibold' : 'regular'}
          >
            {title}
          </Text>
        </InlineStack>
      </button>

      {hasCollapsible && (
        <>
          <div className="checklistItemContent">
            <Collapsible open={isOpen} id={id}>
              <Box paddingBlockEnd="300">
                <BlockStack gap="300">
                  <Text as="p" variant="bodyMd">
                    {description}
                  </Text>
                  {primaryButton || secondaryButton ? (
                    <InlineStack gap="400">
                      {primaryButton && primaryButton.visible !== false ? (
                        <Button variant="primary" {...primaryButton.props}>
                          {primaryButton.content}
                        </Button>
                      ) : null}
                      {secondaryButton && secondaryButton.visible !== false ? (
                        <Button variant="tertiary" {...secondaryButton.props}>
                          {secondaryButton.content}
                        </Button>
                      ) : null}
                    </InlineStack>
                  ) : null}
                </BlockStack>
              </Box>
            </Collapsible>
          </div>

          {illustration && (
            <div className="illustrationCard">
              <div className="illustration">{illustration}</div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

const outlineSvg = (
  <svg
    style={{
      verticalAlign: 'bottom',
    }}
    width="20"
    height="20"
    viewBox="2 2 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>Checkmark</title>
    <path
      style={{
        fill: 'var(--p-color-icon-secondary)',
      }}
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10.5334 2.10692C11.0126 2.03643 11.5024 2 12 2C12.4976 2 12.9874 2.03643 13.4666 2.10692C14.013 2.18729 14.3908 2.6954 14.3104 3.2418C14.23 3.78821 13.7219 4.166 13.1755 4.08563C12.7924 4.02927 12.3999 4 12 4C11.6001 4 11.2076 4.02927 10.8245 4.08563C10.2781 4.166 9.76995 3.78821 9.68958 3.2418C9.6092 2.6954 9.987 2.18729 10.5334 2.10692ZM7.44122 4.17428C7.77056 4.61763 7.67814 5.24401 7.23479 5.57335C6.603 6.04267 6.04267 6.603 5.57335 7.23479C5.24401 7.67814 4.61763 7.77056 4.17428 7.44122C3.73094 7.11188 3.63852 6.4855 3.96785 6.04216C4.55386 5.25329 5.25329 4.55386 6.04216 3.96785C6.4855 3.63852 7.11188 3.73094 7.44122 4.17428ZM16.5588 4.17428C16.8881 3.73094 17.5145 3.63852 17.9578 3.96785C18.7467 4.55386 19.4461 5.25329 20.0321 6.04216C20.3615 6.4855 20.2691 7.11188 19.8257 7.44122C19.3824 7.77056 18.756 7.67814 18.4267 7.23479C17.9573 6.603 17.397 6.04267 16.7652 5.57335C16.3219 5.24401 16.2294 4.61763 16.5588 4.17428ZM3.2418 9.68958C3.78821 9.76995 4.166 10.2781 4.08563 10.8245C4.02927 11.2076 4 11.6001 4 12C4 12.3999 4.02927 12.7924 4.08563 13.1755C4.166 13.7219 3.78821 14.23 3.2418 14.3104C2.6954 14.3908 2.18729 14.013 2.10692 13.4666C2.03643 12.9874 2 12.4976 2 12C2 11.5024 2.03643 11.0126 2.10692 10.5334C2.18729 9.987 2.6954 9.6092 3.2418 9.68958ZM20.7582 9.68958C21.3046 9.6092 21.8127 9.987 21.8931 10.5334C21.9636 11.0126 22 11.5024 22 12C22 12.4976 21.9636 12.9874 21.8931 13.4666C21.8127 14.013 21.3046 14.3908 20.7582 14.3104C20.2118 14.23 19.834 13.7219 19.9144 13.1755C19.9707 12.7924 20 12.3999 20 12C20 11.6001 19.9707 11.2076 19.9144 10.8245C19.834 10.2781 20.2118 9.76995 20.7582 9.68958ZM4.17428 16.5588C4.61763 16.2294 5.24401 16.3219 5.57335 16.7652C6.04267 17.397 6.603 17.9573 7.23479 18.4267C7.67814 18.756 7.77056 19.3824 7.44122 19.8257C7.11188 20.2691 6.4855 20.3615 6.04216 20.0321C5.25329 19.4461 4.55386 18.7467 3.96785 17.9578C3.63852 17.5145 3.73094 16.8881 4.17428 16.5588ZM19.8257 16.5588C20.2691 16.8881 20.3615 17.5145 20.0321 17.9578C19.4461 18.7467 18.7467 19.4461 17.9578 20.0321C17.5145 20.3615 16.8881 20.2691 16.5588 19.8257C16.2294 19.3824 16.3219 18.756 16.7652 18.4267C17.397 17.9573 17.9573 17.397 18.4267 16.7652C18.756 16.3219 19.3824 16.2294 19.8257 16.5588ZM9.68958 20.7582C9.76995 20.2118 10.2781 19.834 10.8245 19.9144C11.2076 19.9707 11.6001 20 12 20C12.3999 20 12.7924 19.9707 13.1755 19.9144C13.7219 19.834 14.23 20.2118 14.3104 20.7582C14.3908 21.3046 14.013 21.8127 13.4666 21.8931C12.9874 21.9636 12.4976 22 12 22C11.5024 22 11.0126 21.9636 10.5334 21.8931C9.987 21.8127 9.6092 21.3046 9.68958 20.7582Z"
    />
    <circle
      className="dotted"
      style={{
        stroke: 'var(--p-color-icon-secondary)',
      }}
      cx="12"
      cy="12"
      r="9"
      strokeWidth="2"
    />
  </svg>
)

const filledSvg = (
  <svg
    style={{
      verticalAlign: 'bottom',
    }}
    width="20"
    height="20"
    viewBox="2 2 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>Checkmark</title>
    <circle
      style={{
        fill: 'var(--p-color-icon-brand)',
      }}
      cx="12"
      cy="12"
      r="10"
      fill="var(--task-manual-completion-bg)"
    />
    <path
      className="tick"
      d="M17.2738 8.52629C17.6643 8.91682 17.6643 9.54998 17.2738 9.94051L11.4405 15.7738C11.05 16.1644 10.4168 16.1644 10.0263 15.7738L7.3596 13.1072C6.96908 12.7166 6.96908 12.0835 7.3596 11.693C7.75013 11.3024 8.38329 11.3024 8.77382 11.693L10.7334 13.6525L15.8596 8.52629C16.2501 8.13577 16.8833 8.13577 17.2738 8.52629Z"
    />
  </svg>
)
